import styled from "@emotion/styled";

import type { MarginProps } from "styled-system";
import { variant } from "styled-system";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import {
  type ButtonHTMLAttributes,
  type ClassAttributes,
  type ForwardedRef,
  forwardRef,
} from "react";
import shouldForwardProp from "@emotion/is-prop-valid";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { css } from "@emotion/react";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";

const sizes = {
  sm: {
    height: 37,
    width: 37,
  },
  md: {
    height: 45,
    width: 45,
  },
};

const variants = {
  outlined: {
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "6px",
  },
  filled: {
    border: "none",
    borderRadius: "6px",
  },
};

type Skin = "info" | "danger" | "success";
type Variant = "outlined" | "filled";

const getButtonColorStyles = ({
  skin,
  disabled,
  theme,
  variant,
}: {
  theme: ExactTheme;
  skin: Skin;
  disabled?: boolean;
  variant?: Variant;
}) => {
  const { colorsV2 } = theme;

  if (disabled) {
    return {
      borderColor: colorsV2.grey[200],
      borderStyle: "solid",
      borderWidth: "1px",
      background: variant === "outlined" ? "transparent" : colorsV2.grey[200],
      color: colorsV2.grey[600],
    };
  }

  const colorName = (
    {
      info: "blue",
      success: "green",
      danger: "red",
    } as const
  )[skin];

  return {
    borderColor: colorsV2[colorName][500],
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "150ms",

    background:
      variant === "outlined" ? "transparent" : colorsV2[colorName][500],
    color: variant === "outlined" ? colorsV2[colorName][500] : "white",
    ":hover": {
      color: "white",
      background: colorsV2[colorName][600],
      borderColor: colorsV2[colorName][600],
    },
    ":focus": {
      outline: "none",
    },
    ":focus-visible": {
      color: "white",
      background: colorsV2[colorName][700],
      borderColor: colorsV2[colorName][700],
      outline: `2px solid ${colorsV2[colorName][100]}`,
    },
  };
};

const buttonTheme = {
  sizes,
  variants,
};

type Size = keyof (typeof buttonTheme)["sizes"];

interface SizeVariationProps {
  size?: Size;
}

type Theme = Omit<ExactTheme, "buttons">;

interface ButtonBaseProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ClassAttributes<HTMLButtonElement>,
    MarginProps<Theme>,
    SizeVariationProps {
  variant?: Variant;
  block?: boolean;
  disabled?: boolean;
  count?: number;
  isLoading?: boolean;
  skin: Skin;
}

export type IconButtonV2Props = ButtonBaseProps;

export const StyledIconButtonV2 = styled("button", {
  shouldForwardProp: (name) => shouldForwardProp(name) && name !== "color",
})<ButtonBaseProps>`
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizesV2.body};
  line-height: 1.25;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadiusV2.primary};
  :focus {
    outline: none;
  }
  ${variant({
    variants: buttonTheme.variants,
  })}
  ${variant({
    prop: "size",
    variants: buttonTheme.sizes,
  })}
  ${getButtonColorStyles}
`;

export const IconButtonV2 = forwardRef(
  (props: IconButtonV2Props, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      isLoading = false,
      size = "md",
      skin = "info",
      variant = "filled",
      type = "button",
      count,
      children,
      ...rest
    } = props;
    return (
      <Skeleton isLoading={isLoading}>
        <StyledIconButtonV2
          ref={ref}
          size={size}
          skin={skin}
          variant={variant}
          type={type}
          {...rest}
        >
          {children}
          {count != null && (
            <div css={countStyles}>
              <TextV2 variant={"body2"}>{count}</TextV2>
            </div>
          )}
        </StyledIconButtonV2>
      </Skeleton>
    );
  },
);

const countStyles = (theme: ExactTheme) =>
  css({
    position: "absolute",
    top: -8,
    right: -1,
    padding: "0 5px",

    display: "flex",
    alignItems: "center",
    height: "17px",
    backgroundColor: "white",
    borderRadius: "10px",
    border: `1px solid ${theme.colorsV2.blue[500]}`,
  });
